import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { File as FileIcon, Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { ragApi } from '@/api/rag';
import { unwrapError } from '@/api/client';
import { RAG_SCOPES, type RagDocumentMetadata, type RagScope } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface Props {
  onUploaded?: () => void;
}

const ACCEPT =
  '.pdf,.txt,.md,.html,.htm,.csv,.doc,.docx,.xls,.xlsx,application/pdf,text/plain,text/markdown,text/html,text/csv';

export function UploadCard({ onUploaded }: Props): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [file, setFile] = React.useState<File | null>(null);
  const [scope, setScope] = React.useState<RagScope>('all');
  const [title, setTitle] = React.useState('');
  const [tagInput, setTagInput] = React.useState('');
  const [tags, setTags] = React.useState<string[]>([]);
  const [progress, setProgress] = React.useState<number | null>(null);
  const [dragOver, setDragOver] = React.useState(false);

  const upload = useMutation({
    mutationFn: async (input: { file: File; metadata: RagDocumentMetadata }) =>
      ragApi.uploadDocument({
        file: input.file,
        metadata: input.metadata,
        onProgress: (loaded, total) => {
          if (total && total > 0) setProgress(Math.round((loaded / total) * 100));
        },
      }),
    onSuccess: (doc) => {
      toast.success(t('rag.uploadedToast', { name: doc.filename }));
      reset();
      void queryClient.invalidateQueries({ queryKey: ['rag', 'docs'] });
      onUploaded?.();
    },
    onError: (err) => {
      setProgress(null);
      toast.error(unwrapError(err));
    },
  });

  function reset(): void {
    setFile(null);
    setTitle('');
    setTags([]);
    setTagInput('');
    setProgress(null);
  }

  function onDrop(e: React.DragEvent): void {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  }

  function commitTag(): void {
    const v = tagInput.trim();
    if (!v || tags.includes(v)) return;
    if (v.length > 64 || tags.length >= 20) return;
    setTags([...tags, v]);
    setTagInput('');
  }

  function submit(): void {
    if (!file) return;
    const metadata: RagDocumentMetadata = {
      scope,
      ...(title.trim() ? { title: title.trim() } : {}),
      ...(tags.length ? { tags } : {}),
    };
    setProgress(0);
    upload.mutate({ file, metadata });
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t('rag.uploadTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={cn(
            'relative rounded-md border-2 border-dashed p-6 text-center transition-colors',
            dragOver ? 'border-primary bg-primary/5' : 'border-input',
          )}
        >
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileIcon className="h-5 w-5 text-primary" />
              <div className="text-left">
                <div className="text-sm font-medium">{file.name}</div>
                <div className="text-xs text-muted-foreground">{formatBytes(file.size)}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setFile(null)} disabled={upload.isPending}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <>
              <Upload className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {t('rag.dragOrBrowsePrefix')}
                <label className="cursor-pointer text-primary hover:underline">
                  {t('rag.browse')}
                  <input
                    type="file"
                    className="sr-only"
                    accept={ACCEPT}
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                </label>
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t('rag.supportedExtensions')}
              </p>
            </>
          )}
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs">{t('rag.scope')} *</Label>
            <Select value={scope} onValueChange={(v: RagScope) => setScope(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RAG_SCOPES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground">{t('rag.scopeHint')}</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="title" className="text-xs">
              {t('rag.titleField')} <span className="text-muted-foreground">{t('rag.titleHint')}</span>
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder={t('rag.titlePlaceholder')}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            {t('rag.tags')} <span className="text-muted-foreground">{t('rag.tagsHint')}</span>
          </Label>
          <div className="flex flex-wrap gap-1.5 rounded-md border p-2">
            {tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="gap-1 pr-1.5">
                {tag}
                <button
                  type="button"
                  onClick={() => setTags(tags.filter((x) => x !== tag))}
                  className="rounded-sm hover:bg-background/50"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            <input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  commitTag();
                } else if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
                  setTags(tags.slice(0, -1));
                }
              }}
              onBlur={commitTag}
              className="min-w-[100px] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              placeholder={tags.length === 0 ? t('rag.tagsPlaceholder') : ''}
              disabled={tags.length >= 20}
            />
          </div>
        </div>

        {progress !== null ? (
          <div className="space-y-1">
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-right text-[10px] text-muted-foreground">{progress}%</p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={reset} disabled={upload.isPending || !file}>
            {t('rag.clear')}
          </Button>
          <Button onClick={submit} disabled={!file || upload.isPending}>
            {upload.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('rag.upload')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}
